import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Users, DollarSign, Ticket, Check, Clock,
  ArrowLeft, Download, RefreshCw, Globe, Calendar, MapPin,
  ChevronDown, Filter, Loader2, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple bar chart component
const SimpleBarChart = ({ data, valueKey, labelKey, color = '#10b981', height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey]), 1);
  
  return (
    <div className="flex items-end gap-1 h-full" style={{ height }}>
      {data.map((item, idx) => {
        const barHeight = (item[valueKey] / maxValue) * height;
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full rounded-t transition-all hover:opacity-80"
              style={{ height: Math.max(barHeight, 2), backgroundColor: color }}
              title={`${item[labelKey]}: ${item[valueKey]}`}
            />
            <span className="text-xs text-gray-500 truncate w-full text-center">
              {item[labelKey]?.slice(-5) || idx}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'green', trend }) => (
  <div className="bg-white rounded-xl p-6 border shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-500 text-sm">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
    {trend && (
      <div className={`flex items-center gap-1 mt-3 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
        <TrendingUp className={`w-4 h-4 ${trend < 0 ? 'rotate-180' : ''}`} />
        <span>{Math.abs(trend)}% from yesterday</span>
      </div>
    )}
  </div>
);

const EventAnalyticsDashboard = () => {
  const { eventId } = useParams();
  
  const [event, setEvent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [checkinAnalytics, setCheckinAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'registrations'
  const [regSearch, setRegSearch] = useState('');
  const [regStatus, setRegStatus] = useState('all');

  const filteredOrders = orders.filter((o) => {
    const q = regSearch.trim().toLowerCase();
    const matchesText = !q || o.buyer_name?.toLowerCase().includes(q) || o.buyer_email?.toLowerCase().includes(q);
    const matchesStatus = regStatus === 'all' || (regStatus === 'paid' ? o.payment_status === 'paid' : o.payment_status !== 'paid');
    return matchesText && matchesStatus;
  });

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, checkinRes, ordersRes] = await Promise.all([
        fetch(`${API}/universal-events/events/${eventId}/analytics`),
        fetch(`${API}/universal-events/events/${eventId}/checkin-analytics`),
        fetch(`${API}/universal-events/events/${eventId}/orders?limit=1000`)
      ]);
      
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
        setEvent(data.event);
      } else {
        toast.error('Failed to load analytics');
      }
      
      if (checkinRes.ok) {
        const checkinData = await checkinRes.json();
        setCheckinAnalytics(checkinData);
      }
      
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData.orders || []);
        setOrdersTotal(ordersData.total || 0);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [eventId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const exportCSV = () => {
    if (!analytics) return;
    
    // Generate CSV content
    let csv = 'Metric,Value\n';
    csv += `Total Orders,${analytics.orders.total}\n`;
    csv += `Confirmed Orders,${analytics.orders.confirmed}\n`;
    csv += `Total Revenue,KES ${analytics.revenue.total}\n`;
    csv += `Total Tickets,${analytics.tickets.total}\n`;
    csv += `Checked In,${analytics.tickets.checked_in}\n`;
    csv += `Check-in Rate,${analytics.tickets.check_in_rate}%\n`;
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${event?.name?.replace(/\s+/g, '_')}_analytics.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Unable to load analytics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="event-analytics">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/admin/events/${eventId}`} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Event Analytics</h1>
                <p className="text-sm text-gray-500">{event?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="text-gray-700 border-gray-300 bg-white hover:bg-gray-100"
                data-testid="analytics-refresh-btn"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" className="text-gray-700 border-gray-300 bg-white hover:bg-gray-100" data-testid="analytics-export-btn" onClick={exportCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Link to={`/admin/events/${eventId}/check-in`}>
                <Button className="bg-green-600 hover:bg-green-700 text-white">
                  <Check className="w-4 h-4 mr-2" />
                  Check-in Scanner
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('registrations')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'registrations' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid="registrations-tab"
            >
              Registrations ({ordersTotal})
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'registrations' ? (
          /* Registrations Tab */
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="registrations-list">
            <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900">All Registrations</h2>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={regSearch}
                    onChange={(e) => setRegSearch(e.target.value)}
                    placeholder="Search name or email"
                    className="pl-9 pr-3 py-2 w-64 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="registrations-search-input"
                  />
                </div>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden" data-testid="registrations-status-filter">
                  {['all', 'paid', 'pending'].map((s) => (
                    <button
                      key={s}
                      onClick={() => setRegStatus(s)}
                      className={`px-3 py-2 text-sm capitalize transition-colors ${regStatus === s ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                      data-testid={`registrations-filter-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <span className="text-sm text-gray-500" data-testid="registrations-count">
                  {filteredOrders.length} of {ordersTotal}
                </span>
              </div>
            </div>
            
            {filteredOrders.length === 0 ? (
              <div className="p-8 text-center text-gray-500" data-testid="registrations-empty">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{orders.length === 0 ? 'No registrations yet' : 'No registrations match your search'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tickets</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{order.buyer_name}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{order.buyer_email}</td>
                        <td className="px-4 py-3">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="text-sm text-gray-600">
                              {item.quantity}x {item.ticket_type}
                            </div>
                          ))}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900" data-testid={`registration-amount-${order.id}`}>KES {order.total_amount?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            order.payment_status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : order.payment_status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {order.payment_status === 'paid' ? (
                              <><Check className="w-3 h-3 mr-1" /> Paid</>
                            ) : (
                              <><Clock className="w-3 h-3 mr-1" /> {order.payment_status}</>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* Overview Tab - Key Metrics */
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={DollarSign}
                label="Total Revenue"
                value={`KES ${analytics.revenue.total.toLocaleString()}`}
                subValue={analytics.revenue.pending > 0 ? `KES ${analytics.revenue.pending.toLocaleString()} pending` : null}
                color="green"
              />
              <StatCard
                icon={Ticket}
                label="Total Orders"
                value={analytics.orders.total}
                subValue={`${analytics.orders.confirmed} confirmed`}
                color="blue"
              />
              <StatCard
            icon={Users}
            label="Total Attendees"
            value={analytics.tickets.total}
            subValue={`${event?.max_attendees ? `of ${event.max_attendees} capacity` : 'No limit set'}`}
            color="purple"
          />
          <StatCard
            icon={Check}
            label="Checked In"
            value={`${analytics.tickets.check_in_rate}%`}
            subValue={`${analytics.tickets.checked_in} of ${analytics.tickets.total}`}
            color="orange"
          />
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue by Ticket Type */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-gray-400" />
              Revenue by Ticket Type
            </h3>
            <div className="space-y-4">
              {analytics.revenue_by_ticket_type.map((tt, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{tt.name}</span>
                      <span className="text-sm text-gray-500">
                        {tt.quantity_sold} sold
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{
                          width: `${tt.quantity_available ? (tt.quantity_sold / tt.quantity_available) * 100 : 50}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      KES {tt.revenue.toLocaleString()} revenue
                    </p>
                  </div>
                </div>
              ))}
              
              {analytics.revenue_by_ticket_type.length === 0 && (
                <p className="text-center text-gray-500 py-4">No ticket sales yet</p>
              )}
            </div>
          </div>

          {/* Check-in Progress */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Check className="w-5 h-5 text-gray-400" />
              Check-in Progress
            </h3>
            
            <div className="relative mb-6">
              <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-green-500 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${analytics.tickets.check_in_rate}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm text-gray-500">
                <span>{analytics.tickets.checked_in} checked in</span>
                <span>{analytics.tickets.not_checked_in} remaining</span>
              </div>
            </div>

            {/* Check-in by Hour */}
            {analytics.check_in_trend?.length > 0 && (
              <>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Check-ins by Hour</h4>
                <SimpleBarChart
                  data={analytics.check_in_trend.slice(6, 22)} // Show 6am to 10pm
                  valueKey="check_ins"
                  labelKey="hour"
                  color="#10b981"
                  height={120}
                />
              </>
            )}
          </div>
        </div>

        {/* Second Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Registration Trend */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gray-400" />
              Registration Trend (Last 30 Days)
            </h3>
            <SimpleBarChart
              data={analytics.registration_trend}
              valueKey="registrations"
              labelKey="date"
              color="#3b82f6"
              height={180}
            />
          </div>

          {/* Demographics */}
          <div className="bg-white rounded-xl p-6 border shadow-sm">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-400" />
              Attendees by Country
            </h3>
            <div className="space-y-3 max-h-[250px] overflow-y-auto">
              {analytics.demographics.map((item, idx) => {
                const percentage = (item.count / analytics.tickets.total) * 100;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="w-24 text-sm font-medium text-gray-900 truncate">
                      {item.country}
                    </span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-500 w-16 text-right">
                      {item.count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                );
              })}
              
              {analytics.demographics.length === 0 && (
                <p className="text-center text-gray-500 py-4">No attendee data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Order Status Breakdown */}
        <div className="bg-white rounded-xl p-6 border shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Order Status Breakdown</h3>
          <div className="grid sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{analytics.orders.confirmed}</p>
              <p className="text-sm text-green-700">Confirmed</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{analytics.orders.pending}</p>
              <p className="text-sm text-yellow-700">Pending</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-600">{analytics.orders.cancelled}</p>
              <p className="text-sm text-red-700">Cancelled</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600">{analytics.orders.total}</p>
              <p className="text-sm text-gray-700">Total</p>
            </div>
          </div>
        </div>

        {/* Check-in Analytics by Time of Day */}
        {checkinAnalytics && (
          <div className="bg-white rounded-xl p-6 border shadow-sm" data-testid="checkin-analytics">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Check-in Rate by Time of Day
            </h3>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{checkinAnalytics.summary.total_checked_in}</p>
                <p className="text-xs text-gray-500">Checked In</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{checkinAnalytics.summary.check_in_rate}%</p>
                <p className="text-xs text-gray-500">Check-in Rate</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {checkinAnalytics.summary.peak_hours?.[0]?.label || 'N/A'}
                </p>
                <p className="text-xs text-gray-500">Peak Hour</p>
              </div>
            </div>
            
            {/* Hourly Bar Chart */}
            <div className="h-48">
              <SimpleBarChart
                data={checkinAnalytics.hourly_breakdown}
                valueKey="count"
                labelKey="label"
                color="#3b82f6"
                height={180}
              />
            </div>
            
            {/* Peak Hours Info */}
            {checkinAnalytics.summary.peak_hours?.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900">Peak Check-in Times:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {checkinAnalytics.summary.peak_hours.map((peak, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {peak.label} ({peak.count} check-ins)
                    </span>
                  ))}
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  Use this data to plan staffing for future events
                </p>
              </div>
            )}
            
            {checkinAnalytics.summary.total_checked_in === 0 && (
              <p className="text-center text-gray-500 py-4">
                No check-ins recorded yet. Data will appear after attendees check in.
              </p>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventAnalyticsDashboard;
